import React from 'react';

const AsList = ({
  filteredRecords,
  handleEditClick,
  handleDownloadRepairReport,
  handleDownloadPDFFromRow,
  handleSettleClick,
  handleDelete
}) => {
  const styles = {
    card: { background: 'white', padding: '40px', borderRadius: '24px', boxShadow: '0 10px 30px rgba(0,0,0,0.04)', marginBottom: '35px', border: '1px solid #edf2f7' },
    sectionTitle: { fontSize: '20px', fontWeight: '800', color: '#1e293b', marginBottom: '30px', display: 'flex', alignItems: 'center', gap: '12px' },
    scrollBox: { maxHeight: '75vh', overflowY: 'auto', border: '1px solid #edf2f7', borderRadius: '15px' },
    // 버튼 자리를 4칸으로 고정한다. 부품판매 줄에는 '정비내역' 이 없는데,
    // 예전에는 그 버튼이 그냥 빠져서 뒤 버튼들이 통째로 왼쪽으로 당겨져 줄이 안 맞았다.
    actionGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 84px)', gap: '5px', alignItems: 'center' },
    // 상태·유형 배지도 같은 이유로 자리를 고정한다 (상태 최대 3자, 유형 최대 4자)
    statusGrid: { display: 'grid', gridTemplateColumns: '54px 64px', gap: '6px', alignItems: 'center' },
  };

  // 상태·유형 배지 공통 바탕 (2026-09-23)
  //   예전에는 상태 배지에만 테두리가 없어서 옆의 유형 배지보다 2px 낮았고,
  //   글자 수(접수 2자 / 진행중 3자)에 따라 폭이 달라져 뒤 배지가 줄마다 다른 자리에서 시작했다.
  //   폭을 고정하고 투명 테두리를 둬서 크기를 맞춘다.
  const badgeBase = {
    padding: '5px 8px',
    borderRadius: '6px',
    fontSize: '11px',
    fontWeight: '800',
    border: '1px solid transparent',
    boxSizing: 'border-box',
    textAlign: 'center',
    whiteSpace: 'nowrap'
  };

  const getStatusBadgeStyle = (status) => {
    if (status === '예약') return { ...badgeBase, background: '#f3e8ff', color: '#9333ea' };
    if (status === '접수') return { ...badgeBase, background: '#fff7ed', color: '#ea580c' };
    if (status === '진행중') return { ...badgeBase, background: '#eff6ff', color: '#2563eb' };
    if (status === '보류') return { ...badgeBase, background: '#fefce8', color: '#ca8a04' };
    return { ...badgeBase, background: '#f0fdf4', color: '#16a34a' };
  };

  // 유형 배지 — 정비 / 부품판매 두 가지뿐이다
  const getCategoryBadgeStyle = (category) => (category === '부품판매'
    ? { ...badgeBase, background: '#fefce8', color: '#ca8a04', borderColor: '#fef08a' }
    : { ...badgeBase, background: '#f8fafc', color: '#64748b', borderColor: '#e2e8f0' });

  // 관리 버튼 색 — 하는 일이 다르면 색도 다르게 (2026-09-23)
  //   예전에는 수정과 정산이 똑같은 파랑, 정비내역과 명세서가 거의 같은 초록이라
  //   네 가지 동작이 두 가지 색을 나눠 쓰고 있었다.
  const BTN = {
    edit:   '#3b82f6', // 파랑  — 수정
    repair: '#0891b2', // 청록  — 정비내역 (열람)
    doc:    '#16a34a', // 초록  — 명세서 (발행)
    settle: '#7c3aed', // 보라  — 정산 (금액 확정)
    remove: '#ef4444'  // 빨강  — 삭제
  };

  // 정산이 끝난(=금액이 잡힌) 건인가
  const isSettled = (r) => r.status === '완료' || r.status === '진행중' || r.status === '보류';

  const getBtnStyle = (color) => ({
    width: '100%',
    boxSizing: 'border-box',
    padding: '7px 6px',
    background: 'white',
    border: `1px solid ${color}`,
    borderRadius: '6px',
    cursor: 'pointer',
    color: color,
    fontSize: '12px',
    fontWeight: '700',
    whiteSpace: 'nowrap',
    textAlign: 'center',
    transition: 'all 0.2s'
  });

  return (
    <div style={styles.card}>
      <div style={styles.sectionTitle}>📊 처리 현황 목록</div>
      <div style={styles.scrollBox}>
        <table style={{width:'100%', borderCollapse:'collapse'}}>
          <thead style={{position:'sticky', top:0, background:'white', zIndex:10}}>
            <tr style={{background:'#f8fafc', borderBottom:'2px solid #eee'}}>
              <th style={{padding:'18px 10px', width: '60px', textAlign: 'center', color: '#64748b', fontSize: '13px'}}>No.</th>
              <th style={{padding:'18px 10px', width: '145px', textAlign: 'left', fontSize: '13px'}}>상태</th>
              <th style={{padding:'18px 10px', width: '130px', textAlign: 'left', fontSize: '13px'}}>접수일/정산일</th>
              
              {/* 🚀 [균형 조정] 고객명 필드에 적절한 고정 넓이(260px)를 부여하여 허전한 빈 공간 방지 */}
              <th style={{padding:'18px 20px', width: '260px', textAlign: 'left', fontSize: '13px'}}>고객명 (차량/유형)</th>
              
              {/* 🚀 [균형 조정] 담당자 필드의 넓이를 살짝 늘려 안정감 부여 */}
              <th style={{padding:'18px 10px', width: '120px', textAlign: 'left', fontSize: '13px'}}>담당자</th>
              
              <th style={{padding:'18px 30px 18px 10px', width: '150px', textAlign: 'right', fontSize: '13px'}}>총 합계(VAT포함)</th>
              
              {/* 🚀 [균형 조정] 남는 화면 여백을 우측 끝으로 자연스럽게 넘기기 위해 넓이(width) 속성 제거 */}
              <th style={{padding:'18px 10px', textAlign:'left', fontSize: '13px'}}>관리</th>
            </tr>
          </thead>
          <tbody>
            {filteredRecords.map((r, index) => (
              <tr key={r.id} style={{borderBottom:'1px solid #f1f5f9'}}>
                {/* No. */}
                <td style={{padding:'16px 10px', textAlign: 'center', fontSize:'13px', color:'#94a3b8'}}>
                  {filteredRecords.length - index}
                </td>
                
                {/* 상태 */}
                <td style={{padding:'16px 10px'}}>
                  <div style={styles.statusGrid}>
                    <span style={getStatusBadgeStyle(r.status || '접수')}>{r.status || '접수'}</span>
                    <span style={getCategoryBadgeStyle(r.serviceCategory || '정비')}>{r.serviceCategory || '정비'}</span>
                  </div>
                </td>
                
                {/* 접수일/정산일 */}
                <td style={{padding:'16px 10px', textAlign: 'left', fontSize:'13px', lineHeight: '1.4'}}>
                  {r.asDate}<br/><span style={{color:'#3b82f6', fontWeight:'bold'}}>{r.settleDate || '-'}</span>
                </td>
                
                {/* 고객명 (차량) */}
                <td style={{padding:'16px 20px', textAlign: 'left'}}>
                  <div style={{fontWeight: 'bold', color: '#0f172a'}}>{r.customerName}</div>
                  <div style={{fontSize:'12px', color:'#94a3b8', marginTop: '2px'}}>{r.carNumber || '단순 납품'}</div>
                </td>
                
                {/* 담당자 */}
                <td style={{padding:'16px 10px', textAlign: 'left', fontSize: '13px'}}>
                  {r.mechanic}
                </td>
                
                {/* 총 합계 */}
                <td style={{padding:'16px 30px 16px 10px', textAlign: 'right', fontWeight: '800', fontSize: '14px', color: '#1e293b'}}>
                  {isSettled(r) ? `₩ ${Number(r.totalCost || 0).toLocaleString()}` : <span style={{color: '#94a3b8', fontSize: '13px', fontWeight: 'bold'}}>정산 대기</span>}
                </td>
                
                {/* 관리 버튼들 */}
                <td style={{padding:'16px 10px'}}>
                  <div style={styles.actionGrid}>
                    <button onClick={()=>handleEditClick(r)} style={getBtnStyle(BTN.edit)}>수정</button>

                    {/* 부품판매에는 정비내역이 없다 — 빈 칸을 남겨 뒤 버튼이 밀리지 않게 한다 */}
                    {r.serviceCategory !== '부품판매'
                      ? <button onClick={()=>handleDownloadRepairReport(r)} style={getBtnStyle(BTN.repair)}>정비내역</button>
                      : <span />}

                    {isSettled(r)
                      ? <button onClick={()=>handleDownloadPDFFromRow(r)} style={getBtnStyle(BTN.doc)}>
                          {r.serviceCategory === '부품판매' ? '거래명세서' : '정비명세서'}
                        </button>
                      : <button onClick={()=>handleSettleClick(r)} style={getBtnStyle(BTN.settle)}>정산</button>}

                    <button onClick={()=>handleDelete(r.id)} style={getBtnStyle(BTN.remove)}>삭제</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AsList;